import type { View } from '../router/router';
import { router } from '../router/router';
import { apiClient } from '../services/api-client';
import { authService } from '../services/auth.service';
import { toast } from '../components/layout/toast';
import { $ } from '../utils/dom';
import { confirmDialog } from '../components/layout/confirm-dialog';
import {
  formatDate,
  licenseBadgeClass,
  licenseLabel,
  paymentBadgeClass,
  paymentDisplayStatus,
  paymentStatusLabel,
  type LicenseType,
  type StudentPaymentStatus,
} from '../utils/admin-labels';
import { renderWorkoutSessionCard, type WorkoutSession } from '../utils/workout-formatter';
import { getTyrisIcon } from '../components/ui/tyris-icon';

interface StudentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  licenseType: LicenseType;
  createdAt: string;
  role?: string;
  dateOfBirth?: string;
  sex?: string;
  phone?: string;
  isActive?: boolean;
}

interface PaymentRecord {
  id: string;
  amount: number;
  paymentDate: string;
  periodStart: string;
  periodEnd: string;
  status: string;
}

interface Routine {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
}

interface AttendanceStats {
  weeklyCount: number;
  monthlyCount: number;
  allTimeCount: number;
}

interface PhysicalMeasure {
  id: string;
  weight: number;
  height: number;
  bodyFatPercentage?: number;
  muscleMass?: number;
  waist?: number;
  date: string;
}

interface PersonalRecord {
  id: string;
  weight: number;
  reps: number;
  date: string;
  exercise: { id: string; name: string };
}

export class AdminStudentDetailPage implements View {
  private studentId = '';
  private student: StudentUser | null = null;
  private paymentStatus: StudentPaymentStatus | null = null;
  private payments: PaymentRecord[] = [];
  private activeRoutines: Routine[] = [];
  private attendanceStats: AttendanceStats | null = null;
  private latestMeasure: PhysicalMeasure | null = null;
  private prs: PersonalRecord[] = [];
  private workoutSessions: WorkoutSession[] = [];
  private isDeletingId: string | null = null;
  private workoutSessionsPage = 1;
  private workoutSessionsPerPage = 5;
  private loading = true;
  private errorMsg: string | null = null;
  private isEditing = false;
  private isSubmittingPayment = false;

  async render(): Promise<string> {
    const params = router.getParams();
    this.studentId = params.id ?? '';

    if (!this.studentId) {
      return `
        <div class="card-glass admin-state-error" data-testid="admin-student-missing-id">
          <h3>Alumno no especificado</h3>
          <p>Selecciona un alumno desde el listado.</p>
          <a href="#/admin/students" class="btn btn-primary">← Volver al listado</a>
        </div>
      `;
    }

    if (this.loading) {
      await this.loadData();
    }

    if (this.loading) {
      return `<div class="admin-state-loading" data-testid="admin-student-loading"><p>Cargando alumno...</p></div>`;
    }

    if (this.errorMsg || !this.student) {
      return `
        <div class="card-glass admin-state-error" data-testid="admin-student-error">
          <h3>Error al cargar alumno</h3>
          <p>${this.errorMsg ?? 'No encontrado'}</p>
          <a href="#/admin/students" class="btn btn-outline">← Volver</a>
        </div>
      `;
    }

    const s = this.student;
    const ps = this.paymentStatus;
    const payDisplay = ps ? paymentDisplayStatus(ps) : 'NONE';
    const isAdmin = authService.hasRole(['ADMIN']);
    const isStaff = authService.hasRole(['ADMIN', 'COACH']);
    const isStudent = !s.role || s.role === 'STUDENT';

    let basicDetailsCard = '';
    if (this.isEditing) {
      basicDetailsCard = `
        <section class="card-glass" data-testid="admin-student-basic-edit">
          <h3 class="admin-section-title" style="display:flex;align-items:center;gap:0.5rem;">${getTyrisIcon('editar', { size: 18 })} Editar Datos del Usuario</h3>
          <form id="form-edit-basic" style="display:flex; flex-direction:column; gap:0.75rem;">
            <div style="display:grid; grid-template-columns:repeat(auto-fit, minmax(200px, 1fr)); gap:1rem;">
              <label style="display:flex; flex-direction:column; gap:0.4rem; font-size:0.85rem; font-weight:600;">Nombre *
                <input name="firstName" type="text" class="admin-input" value="${s.firstName}" required minlength="2" style="margin-top:0.25rem;" />
              </label>
              <label style="display:flex; flex-direction:column; gap:0.4rem; font-size:0.85rem; font-weight:600;">Apellido *
                <input name="lastName" type="text" class="admin-input" value="${s.lastName}" required minlength="2" style="margin-top:0.25rem;" />
              </label>
              <label style="display:flex; flex-direction:column; gap:0.4rem; font-size:0.85rem; font-weight:600;">Correo Electrónico *
                <input name="email" type="email" class="admin-input" value="${s.email}" required style="margin-top:0.25rem;" />
              </label>
              <label style="display:flex; flex-direction:column; gap:0.4rem; font-size:0.85rem; font-weight:600;">Contraseña de Acceso
                <input name="password" type="text" class="admin-input" placeholder="Dejar vacío para no cambiar" minlength="6" style="margin-top:0.25rem;" />
              </label>
              <label style="display:flex; flex-direction:column; gap:0.4rem; font-size:0.85rem; font-weight:600;">Teléfono
                <input name="phone" type="tel" class="admin-input" value="${s.phone ?? ''}" placeholder="ej: 099123456" style="margin-top:0.25rem;" />
              </label>
              <label style="display:flex; flex-direction:column; gap:0.4rem; font-size:0.85rem; font-weight:600;">Sexo
                <select name="sex" class="admin-select" style="margin-top:0.25rem; height:2.5rem;">
                  <option value="" ${!s.sex ? 'selected' : ''}>— Sin especificar —</option>
                  <option value="M" ${s.sex === 'M' ? 'selected' : ''}>Masculino</option>
                  <option value="F" ${s.sex === 'F' ? 'selected' : ''}>Femenino</option>
                  <option value="O" ${s.sex === 'O' ? 'selected' : ''}>Otro</option>
                </select>
              </label>
              <label style="display:flex; flex-direction:column; gap:0.4rem; font-size:0.85rem; font-weight:600;">Fecha de Nacimiento
                <input name="dateOfBirth" type="date" class="admin-input" value="${s.dateOfBirth ? s.dateOfBirth.split('T')[0] : ''}" style="margin-top:0.25rem;" />
              </label>
              ${isAdmin ? `
              <label style="display:flex; flex-direction:column; gap:0.4rem; font-size:0.85rem; font-weight:600;">Rol
                <select name="role" class="admin-select" style="margin-top:0.25rem; height:2.5rem;" required>
                  <option value="STUDENT" ${s.role === 'STUDENT' ? 'selected' : ''}>Alumno (STUDENT)</option>
                  <option value="COACH" ${s.role === 'COACH' ? 'selected' : ''}>Profesor (COACH)</option>
                  <option value="ADMIN" ${s.role === 'ADMIN' ? 'selected' : ''}>Administrador (ADMIN)</option>
                </select>
              </label>
              ` : ''}
            </div>
            <div style="display:flex; gap:0.75rem; margin-top:1.5rem;">
              <button type="submit" class="btn btn-primary" data-testid="edit-basic-save-btn">Guardar Cambios</button>
              <button type="button" id="btn-cancel-edit-basic" class="btn btn-outline">Cancelar</button>
            </div>
          </form>
        </section>
      `;
    } else {
      basicDetailsCard = `
        <section class="card-glass" data-testid="admin-student-basic">
          <div class="admin-profile-head">
            <span class="admin-profile-avatar">${getTyrisIcon(isStudent ? 'perfil' : (s.role === 'ADMIN' ? 'configurar' : 'consejo'), { size: 22 })}</span>
            <div class="admin-profile-title">
              <h3>${s.firstName} ${s.lastName}</h3>
              <p>${s.email}</p>
            </div>
            ${isAdmin ? `<button id="btn-edit-basic" class="btn btn-primary btn-sm" data-testid="edit-basic-btn">${getTyrisIcon('editar', { size: 14 })} Editar</button>` : ''}
          </div>
          <div class="admin-profile-badges">
            <span class="badge ${s.role && s.role !== 'STUDENT' ? `badge-${s.role.toLowerCase()}` : 'badge-student'}">${s.role ?? 'STUDENT'}</span>
            <span class="badge ${s.isActive === false ? 'badge-error' : 'badge-success'}">${s.isActive === false ? 'Baja' : 'Activo'}</span>
          </div>
          <dl class="admin-dl">
            <dt>Registro</dt><dd>${formatDate(s.createdAt)}</dd>
            ${s.dateOfBirth ? `<dt>Fecha nacimiento</dt><dd>${new Date(s.dateOfBirth).toLocaleDateString('es-UY', { timeZone: 'UTC' })}</dd>` : ''}
            ${s.sex ? `<dt>Sexo</dt><dd>${s.sex === 'M' ? 'Masculino' : s.sex === 'F' ? 'Femenino' : 'Otro'}</dd>` : ''}
            ${s.phone ? `<dt>Teléfono</dt><dd>${s.phone}</dd>` : ''}
          </dl>
        </section>
      `;
    }

    const studentSections = isStudent ? `
      <div class="admin-detail-grid" style="margin-top:1.5rem;">
        <section class="card-glass" data-testid="admin-student-license">
          <h3 class="admin-section-title">Licencia</h3>
          <p style="margin-bottom:1rem;">
            <span class="${licenseBadgeClass(s.licenseType)}" data-testid="admin-student-license-badge">
              ${licenseLabel(s.licenseType)}
            </span>
          </p>
          ${isStaff ? `
          <form id="form-license" class="admin-inline-form">
            <select id="license-select" class="admin-select" data-testid="license-select">
              <option value="NONE" ${s.licenseType === 'NONE' ? 'selected' : ''}>Sin licencia</option>
              <option value="SEMI_PERSONALIZED" ${s.licenseType === 'SEMI_PERSONALIZED' ? 'selected' : ''}>Semi-personalizado</option>
              <option value="PERSONALIZED" ${s.licenseType === 'PERSONALIZED' ? 'selected' : ''}>Personalizado</option>
            </select>
            <button type="submit" class="btn btn-primary btn-sm" data-testid="license-save-btn">Guardar licencia</button>
          </form>
          ` : `<p class="admin-cell-muted" style="font-size:0.8rem;display:inline-flex;align-items:center;gap:0.3rem;">${getTyrisIcon('darse-de-baja', { size: 14 })} Solo ADMIN o COACH puede cambiar la licencia.</p>`}
        </section>

        ${isAdmin ? `
        <section class="card-glass" data-testid="admin-student-payment-status">
          <div class="admin-pay-head">
            <h3 class="admin-section-title">Estado de pago</h3>
            <span class="${paymentBadgeClass(payDisplay)}" data-testid="admin-student-payment-badge">
              ${paymentStatusLabel(payDisplay)}
            </span>
          </div>
          <div class="admin-pay-due" data-testid="admin-student-next-due">
            <span class="admin-pay-due-label">Próximo vencimiento</span>
            <span class="admin-pay-due-value">${formatDate(ps?.nextDueDate)}</span>
          </div>
          ${ps?.daysUntilDue !== null && ps?.daysUntilDue !== undefined ? this.renderDueProgress(ps.daysUntilDue) : ''}
        </section>
        ` : ''}

        <section class="card-glass" data-testid="admin-student-routine">
          <h3 class="admin-section-title">Rutinas activas (${this.activeRoutines.length}/5)</h3>
          ${this.activeRoutines.length > 0
          ? this.activeRoutines.map(r => `
              <div style="margin-bottom:0.75rem;">
                <p><strong>${r.name}</strong></p>
                <p class="admin-cell-muted">${r.description ?? 'Sin descripción'}</p>
                <div class="admin-quick-links" style="margin-top:0.5rem;">
                  <a href="#/admin/routine?id=${r.id}&from=student&studentId=${s.id}" class="btn btn-outline btn-sm" data-testid="admin-student-active-routine-link">Ver rutina →</a>
                </div>
              </div>`).join('<hr style="border-color:var(--border-metallic);margin:0.75rem 0;">')
          : `<p class="admin-cell-muted">Sin rutinas activas asignadas.</p>`
        }
          <div class="admin-quick-links" style="margin-top:1rem;">
          <a href="#/admin/routines?studentId=${s.id}" class="btn btn-outline btn-sm" data-testid="admin-student-routines-link" style="display:inline-flex;align-items:center;gap:0.4rem;">${getTyrisIcon('mis-rutinas', { size: 14 })} Rutinas del alumno</a>
          <a href="#/admin/routine-templates" class="btn btn-outline btn-sm" data-testid="admin-student-templates-link" style="display:inline-flex;align-items:center;gap:0.4rem;">${getTyrisIcon('mis-rutinas', { size: 14 })} Templates</a>
          <a href="#/admin/routine?form=create&studentId=${s.id}" class="btn btn-primary btn-sm" data-testid="admin-student-new-routine-link" style="display:inline-flex;align-items:center;gap:0.4rem;">${getTyrisIcon('plus', { size: 14 })} Nueva rutina</a>
          </div>
        </section>
      </div>

      <section class="card-glass" style="margin-top:1.5rem;" data-testid="admin-student-quick-links">
        <h3 class="admin-section-title">Seguimiento del alumno</h3>
        <div class="admin-quick-links">
          <a href="#/admin/measures?studentId=${s.id}" class="btn btn-outline" data-testid="admin-student-measures-link" style="display:inline-flex;align-items:center;gap:0.4rem;">${getTyrisIcon('medidas', { size: 16 })} Medidas corporales</a>
          <a href="#/admin/attendance?studentId=${s.id}" class="btn btn-outline" data-testid="admin-student-attendance-link" style="display:inline-flex;align-items:center;gap:0.4rem;">${getTyrisIcon('asistencia', { size: 16 })} Asistencia detallada</a>
          <a href="#/admin/rankings" class="btn btn-outline" data-testid="admin-student-rankings-link" style="display:inline-flex;align-items:center;gap:0.4rem;">${getTyrisIcon('rankings', { size: 16 })} Rankings</a>
          ${isAdmin ? `<a href="#/admin/payments?highlight=${s.id}" class="btn btn-outline" style="display:inline-flex;align-items:center;gap:0.4rem;">${getTyrisIcon('pagos', { size: 16 })} Pagos del gimnasio</a>` : ''}
        </div>
        ${this.attendanceStats
        ? `
          <p style="margin-top:1rem;color:var(--text-muted);font-size:0.9rem;" data-testid="admin-student-attendance-summary">
            Asistencia: ${this.attendanceStats.weeklyCount} esta semana · ${this.attendanceStats.monthlyCount} este mes · ${this.attendanceStats.allTimeCount} total
          </p>
        `
        : ''
      }
      </section>

      ${isAdmin ? `
      <section class="card-glass" style="margin-top:1.5rem;" data-testid="admin-student-register-payment">
        <h3 class="admin-section-title">Registrar pago del mes (manual)</h3>
        <p class="admin-cell-muted" style="margin-bottom:1rem;">
          Crea un período de membresía vía <code>POST /api/payments</code>. El backend evita solapamientos de fechas.
        </p>
        <form id="form-payment" class="admin-payment-form">
          <div class="admin-form-grid">
            <label>Monto ($)
              <input type="number" name="amount" class="admin-input" min="0.01" step="0.01" value="50" required />
            </label>
            <label>Inicio período
              <input type="date" name="periodStart" class="admin-input" required />
            </label>
            <label>Fin período
              <input type="date" name="periodEnd" class="admin-input" required />
            </label>
          </div>
          <button type="submit" class="btn btn-primary" data-testid="register-payment-btn">Marcar pago como pagado</button>
        </form>
      </section>

      <section class="card-glass admin-table-wrap" style="margin-top:1.5rem;" data-testid="admin-student-payments-history">
        <h3 class="admin-section-title">Historial de pagos</h3>
        ${this.payments.length === 0
          ? `<div class="admin-empty"><p>Sin pagos registrados para este alumno.</p></div>`
          : `
          <table class="admin-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Período</th>
                <th>Monto</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              ${this.payments
            .map(
              (p) => `
                <tr>
                  <td>${formatDate(p.paymentDate)}</td>
                  <td>${formatDate(p.periodStart)} – ${formatDate(p.periodEnd)}</td>
                  <td>$${p.amount.toFixed(2)}</td>
                  <td><span class="badge-payment badge-payment-${p.status === 'PAID' ? 'active' : p.status === 'OVERDUE' ? 'overdue' : 'none'}">${p.status}</span></td>
                </tr>
              `,
            )
            .join('')}
            </tbody>
          </table>
        `
        }
      </section>
      ` : ''}

      <div class="admin-detail-grid" style="margin-top:1.5rem;">
        <section class="card-glass" data-testid="admin-student-measures-summary">
          <h3 class="admin-section-title" style="display:flex;align-items:center;gap:0.5rem;">${getTyrisIcon('medidas', { size: 18 })} Última medida corporal</h3>
          ${this.latestMeasure
        ? `
            <dl class="admin-dl">
              <dt>Fecha</dt><dd>${formatDate(this.latestMeasure.date)}</dd>
              <dt>Peso</dt><dd>${this.latestMeasure.weight} kg</dd>
              <dt>Altura</dt><dd>${this.latestMeasure.height} cm</dd>
              ${this.latestMeasure.bodyFatPercentage != null ? `<dt>% Grasa</dt><dd>${this.latestMeasure.bodyFatPercentage}%</dd>` : ''}
              ${this.latestMeasure.muscleMass != null ? `<dt>Masa musc.</dt><dd>${this.latestMeasure.muscleMass} kg</dd>` : ''}
              ${this.latestMeasure.waist != null ? `<dt>Cintura</dt><dd>${this.latestMeasure.waist} cm</dd>` : ''}
            </dl>
            <a href="#/admin/measures?studentId=${s.id}" class="btn btn-outline btn-sm" style="margin-top:0.75rem;">Ver historial completo →</a>
          `
        : `<p class="admin-cell-muted">Sin medidas registradas.</p>
            <a href="#/admin/measures?studentId=${s.id}" class="btn btn-outline btn-sm" style="margin-top:0.75rem;">Registrar medida →</a>`
      }
        </section>

        <section class="card-glass" data-testid="admin-student-prs-summary">
          <h3 class="admin-section-title" style="display:flex;align-items:center;gap:0.5rem;">${getTyrisIcon('mis-records', { size: 18 })} Récords personales (PRs)</h3>
          ${this.prs.length === 0
        ? `<p class="admin-cell-muted">Sin PRs registrados.</p>`
        : `
            <table class="admin-table" style="font-size:0.85rem;">
              <thead><tr><th>Ejercicio</th><th>Peso</th><th>Reps</th><th>Fecha</th></tr></thead>
              <tbody>
                ${this.prs
          .slice(0, 5)
          .map(
            (pr) => `
                  <tr>
                    <td>${pr.exercise.name}</td>
                    <td>${pr.weight} kg</td>
                    <td>${pr.reps}</td>
                    <td class="admin-cell-muted">${formatDate(pr.date)}</td>
                  </tr>
                `,
          )
          .join('')}
              </tbody>
            </table>
            ${this.prs.length > 5 ? `<p class="admin-cell-muted" style="margin-top:0.5rem;">+${this.prs.length - 5} más</p>` : ''}
          `
      }
          <a href="#/admin/rankings" class="btn btn-outline btn-sm" style="margin-top:0.75rem;">Ver rankings →</a>
        </section>
      </div>

      <!-- Historial de Entrenamientos -->
      <section class="card-glass" style="margin-top:1.5rem;" data-testid="admin-student-workout-history">
        <h3 class="admin-section-title" style="display:flex;align-items:center;gap:0.5rem;">${getTyrisIcon('comenzar-entrenamiento', { size: 18 })} Historial de Entrenamientos</h3>
        ${this.workoutSessions.length === 0
          ? `<div class="admin-empty" style="text-align:center;padding:2rem;"><p style="color:var(--text-muted);margin:0;">Sin sesiones registradas para este alumno.</p></div>`
          : (() => {
              const totalPages = Math.ceil(this.workoutSessions.length / this.workoutSessionsPerPage) || 1;
              if (this.workoutSessionsPage > totalPages) this.workoutSessionsPage = totalPages;
              if (this.workoutSessionsPage < 1) this.workoutSessionsPage = 1;

              const startIndex = (this.workoutSessionsPage - 1) * this.workoutSessionsPerPage;
              const endIndex = startIndex + this.workoutSessionsPerPage;
              const paginatedSessions = this.workoutSessions.slice(startIndex, endIndex);

              const paginationHtml = this.workoutSessions.length > this.workoutSessionsPerPage
                ? `
                  <div style="display:flex;justify-content:center;align-items:center;gap:1.5rem;margin-top:1.5rem;padding:0.5rem 0;">
                    <button id="admin-prev-workout-page" class="btn btn-outline btn-sm" ${this.workoutSessionsPage === 1 ? 'disabled' : ''} style="width:auto;padding:0.4rem 1rem;display:flex;align-items:center;gap:0.35rem;">
                      ← Anterior
                    </button>
                    <span style="font-size:0.85rem;color:var(--text-muted);user-select:none;">
                      Página <strong>${this.workoutSessionsPage}</strong> de <strong>${totalPages}</strong>
                    </span>
                    <button id="admin-next-workout-page" class="btn btn-outline btn-sm" ${this.workoutSessionsPage === totalPages ? 'disabled' : ''} style="width:auto;padding:0.4rem 1rem;display:flex;align-items:center;gap:0.35rem;">
                      Siguiente →
                    </button>
                  </div>
                `
                : '';

              return `
                <div style="display:flex;flex-direction:column;gap:1.25rem;margin-top:1rem;">
                  ${paginatedSessions.map(session => renderWorkoutSessionCard(session, { showDeleteButton: true, isDeletingId: this.isDeletingId ?? undefined })).join('')}
                </div>
                ${paginationHtml}
              `;
            })()
        }
      </section>

    ` : '';

    return `
      <div class="dashboard-header">
        <div class="dashboard-title">
          <nav class="admin-breadcrumb">
            <a href="#/admin/students">${isAdmin ? 'Usuarios' : 'Alumnos'}</a>
            <span class="admin-breadcrumb-sep">›</span>
            <span>${s.firstName} ${s.lastName}</span>
          </nav>
          <h2 data-testid="admin-student-name">${s.firstName} ${s.lastName}</h2>
          <p>${s.email}</p>
        </div>
      </div>

      <div class="admin-detail-grid ${!isStudent ? 'admin-detail-grid--single' : ''}">
        ${basicDetailsCard}
      </div>

      ${studentSections}
    `;
  }

  /** Barra de proximidad al vencimiento (ciclo mensual de 30 días) */
  private renderDueProgress(daysUntilDue: number): string {
    const safeDays = Math.max(daysUntilDue, 0);
    const pct = Math.min((safeDays / 30) * 100, 100);
    const tone = safeDays <= 3 ? 'danger' : safeDays <= 7 ? 'warning' : 'ok';
    const label =
      safeDays <= 3
        ? '¡Vence esta semana!'
        : safeDays <= 7
          ? 'Vence en los próximos días'
          : 'Membresía al día';
    return `
      <div class="admin-pay-bar">
        <div class="admin-pay-bar-track">
          <div class="admin-pay-bar-fill admin-pay-bar--${tone}" style="width:${pct}%;"></div>
        </div>
        <div class="admin-pay-bar-meta">
          <span>${safeDays === 0 ? 'Vence hoy' : `${safeDays} día${safeDays !== 1 ? 's' : ''} restante${safeDays !== 1 ? 's' : ''}`}</span>
          <span>${label}</span>
        </div>
      </div>
    `;
  }

  afterRender(): void {
    const renderInline = async () => {
      const viewArea = $('#app-view-area');
      if (viewArea) {
        viewArea.innerHTML = await this.render();
        this.afterRender();
      }
    };

    // Toggle edit basic
    const editBasicBtn = $('#btn-edit-basic');
    editBasicBtn?.addEventListener('click', () => {
      this.isEditing = true;
      renderInline();
    });

    const cancelEditBasicBtn = $('#btn-cancel-edit-basic');
    cancelEditBasicBtn?.addEventListener('click', () => {
      this.isEditing = false;
      renderInline();
    });

    // Form basic submit
    const formEditBasic = $('#form-edit-basic');
    formEditBasic?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const form = e.target as HTMLFormElement;
      const fd = new FormData(form);
      const firstName = String(fd.get('firstName')).trim();
      const lastName = String(fd.get('lastName')).trim();
      const email = String(fd.get('email')).trim();
      const password = String(fd.get('password')).trim();
      const phone = String(fd.get('phone')).trim();
      const sex = String(fd.get('sex')).trim();
      const dateOfBirth = String(fd.get('dateOfBirth')).trim();
      const role = fd.get('role') ? String(fd.get('role')) : undefined;

      const payload: any = {
        firstName,
        lastName,
        email,
        phone: phone || null,
        sex: sex || null,
        dateOfBirth: dateOfBirth ? new Date(dateOfBirth).toISOString() : null,
      };

      if (password) {
        payload.password = password;
      }
      if (role) {
        payload.role = role;
      }

      try {
        await apiClient.patch(`/api/users/${this.studentId}`, payload);
        toast.success('Datos actualizados correctamente.');
        this.isEditing = false;
        this.loading = true;
        await this.loadData();
        renderInline();
      } catch (err: any) {
        const body = err?.body;
        const msg = Array.isArray(body?.message) ? body.message.join(', ') : body?.message;
        toast.error(msg ?? 'No se pudieron guardar los cambios.');
      }
    });

    const isStudent = this.student && (!this.student.role || this.student.role === 'STUDENT');

    if (isStudent) {
      this.setDefaultPaymentDates();

      const licenseForm = $('#form-license');
      licenseForm?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const select = $('#license-select') as HTMLSelectElement | null;
        if (!select || !this.studentId) return;

        try {
          await apiClient.patch(`/api/users/${this.studentId}/license`, {
            licenseType: select.value,
          });
          toast.success('Licencia actualizada correctamente.');
          this.loading = true;
          await this.loadData();
          renderInline();
        } catch (err: unknown) {
          const body = (err as { body?: { message?: string } })?.body;
          toast.error(body?.message ?? 'No se pudo actualizar la licencia.');
        }
      });

      const paymentForm = $('#form-payment');
      paymentForm?.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!this.studentId) return;
        // Guard: evitar doble envío por doble-clic o lentitud de red
        if (this.isSubmittingPayment) return;
        this.isSubmittingPayment = true;

        const submitBtn = paymentForm.querySelector('[data-testid="register-payment-btn"]') as HTMLButtonElement | null;
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.textContent = 'Registrando...';
        }

        const form = e.target as HTMLFormElement;
        const fd = new FormData(form);
        const amount = Number(fd.get('amount'));
        const periodStart = String(fd.get('periodStart'));
        const periodEnd = String(fd.get('periodEnd'));

        try {
          await apiClient.post('/api/payments', {
            studentId: this.studentId,
            amount,
            periodStart,
            periodEnd,
            status: 'PAID',
          });
          toast.success('Pago registrado correctamente.');
          this.loading = true;
          await this.loadData();
          renderInline();
        } catch (err: unknown) {
          const body = (err as { body?: { message?: string } })?.body;
          toast.error(body?.message ?? 'No se pudo registrar el pago.');
          // Restaurar botón solo en error (en éxito, renderInline recrea el DOM)
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = 'Marcar pago como pagado';
          }
        } finally {
          this.isSubmittingPayment = false;
        }
      });

      // Delete workout session handler
      document.querySelectorAll('.btn-delete-workout').forEach((btn) => {
        btn.addEventListener('click', async (e) => {
          const target = e.currentTarget as HTMLButtonElement;
          const sessionId = target.dataset.sessionId;
          if (!sessionId) return;

          const confirmed = await confirmDialog({
            title: '¿Eliminar sesión?',
            message: 'Esta acción no se puede deshacer y se eliminarán todas las series registradas.',
            confirmLabel: 'Sí, eliminar',
            cancelLabel: 'Cancelar',
            variant: 'danger',
          });
          if (!confirmed) return;

          try {
            this.isDeletingId = sessionId;
            renderInline();

            await apiClient.delete(`/api/workout-sessions/${sessionId}`);
            toast.success('Sesión de entrenamiento eliminada correctamente.');

            this.workoutSessions = this.workoutSessions.filter((s) => s.id !== sessionId);
          } catch (err: any) {
            const body = err?.body;
            const msg = Array.isArray(body?.message) ? body.message.join(', ') : body?.message;
            toast.error(msg ?? 'No se pudo eliminar la sesión de entrenamiento.');
          } finally {
            this.isDeletingId = null;
            renderInline();
          }
        });
      });

      // Pagination handlers for workout sessions
      const prevWorkoutBtn = document.getElementById('admin-prev-workout-page');
      prevWorkoutBtn?.addEventListener('click', () => {
        if (this.workoutSessionsPage > 1) {
          this.workoutSessionsPage--;
          renderInline();
        }
      });

      const nextWorkoutBtn = document.getElementById('admin-next-workout-page');
      nextWorkoutBtn?.addEventListener('click', () => {
        const totalPages = Math.ceil(this.workoutSessions.length / this.workoutSessionsPerPage);
        if (this.workoutSessionsPage < totalPages) {
          this.workoutSessionsPage++;
          renderInline();
        }
      });
    }

  }

  private setDefaultPaymentDates(): void {
    const startInput = document.querySelector(
      '#form-payment input[name="periodStart"]',
    ) as HTMLInputElement | null;
    const endInput = document.querySelector(
      '#form-payment input[name="periodEnd"]',
    ) as HTMLInputElement | null;
    if (!startInput || !endInput) return;

    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);

    const toIsoDate = (d: Date) => d.toISOString().split('T')[0];
    if (!startInput.value) startInput.value = toIsoDate(first);
    if (!endInput.value) endInput.value = toIsoDate(last);
  }

  private async loadData(): Promise<void> {
    try {
      const summary = await apiClient.get<any>(`/api/users/${this.studentId}/summary`);

      this.student = summary.user as StudentUser;
      this.activeRoutines = (summary.routines ?? []).filter((r: any) => r.isActive);
      this.attendanceStats = summary.attendanceStats ?? null;
      this.latestMeasure = summary.latestMeasures?.[0] ?? null;
      this.prs = summary.prs ?? [];
      this.paymentStatus = summary.paymentSummary ? {
        studentId: this.studentId,
        firstName: this.student!.firstName,
        lastName: this.student!.lastName,
        email: this.student!.email,
        isActive: summary.paymentSummary.status === 'ACTIVE' || summary.paymentSummary.status === 'UPCOMING',
        latestPayment: summary.payments?.[0] ?? null,
        nextDueDate: summary.paymentSummary.nextDueDate,
        daysUntilDue: summary.paymentSummary.daysUntilDue,
      } as any : null;
      this.payments = summary.payments ?? [];

      this.workoutSessions = [];
      const isStudent = !this.student.role || this.student.role === 'STUDENT';
      if (isStudent) {
        try {
          this.workoutSessions = await apiClient.get<WorkoutSession[]>(`/api/workout-sessions/student/${this.studentId}`);
        } catch (err: any) {
          console.error('Error al cargar el historial de entrenamientos:', err);
        }
      }

      this.errorMsg = null;
    } catch (err: unknown) {
      this.errorMsg = err instanceof Error ? err.message : 'Error al cargar alumno';
    } finally {
      this.loading = false;
    }
  }
}
